// Asking the messenger for the user's phone number — as a *convenience only*.
//
// All three SDKs expose requestContact(), which shows the client's own native
// "share your number" sheet and hands the number back to this page.
//
// The platforms answer in two different shapes, and the difference decides
// whether the number can be trusted:
//
// Eitaa / Telegram
//     callback(sent, event) where `event.response` is a SEPARATELY SIGNED
//     payload — `contact=<json>&auth_date=…&hash=…`, signed with the same bot
//     token as initData. The backend verifies that signature, so the number is
//     proven and the user can be signed straight in. No SMS.
//
// Bale
//     callback(sent, "09121234567") — a bare string with no signature at all.
//     The page could have made it up, and so could anyone who opens the site
//     outside a messenger. So on Bale it may ONLY pre-fill the login field;
//     the SMS code still does the proving. Bale users get an equivalent
//     zero-SMS path through the bot's shared-contact button instead.

import posthog from 'posthog-js'

// `toLocalMobile` lives in `utilities/phone` now: the login field is no longer
// the only thing that needs one spelling of a phone number — analytics keys its
// person on it too, and the two must agree or the same user becomes two people.
import { toLocalMobile } from 'utilities/phone'
import { resolvePlatform } from './detect'
import { loadSdk } from './sdk'

export { toLocalMobile } from 'utilities/phone'

const REQUEST_TIMEOUT_MS = 60000

/**
 * Why a client refused `requestContact()`, as one of four values.
 *
 * The message itself used to be sent verbatim. It is free text from a
 * third-party SDK we do not control — it can carry anything, including the
 * number the user just shared — and as the only unbounded string in the
 * taxonomy it was also the one prop that could not be grouped by. The three
 * documented refusals are the three that need telling apart; everything else
 * is `other` and belongs in a bug report, not in analytics.
 */
type ContactErrorKind =
    | 'unsupported_client'
    | 'already_in_flight'
    | 'rate_limited'
    | 'other'

const classifyContactError = (error: unknown): ContactErrorKind => {
    const message = String(
        (error as Error)?.message || error || ''
    ).toLowerCase()
    if (/unsupport|not supported|version/.test(message)) {
        return 'unsupported_client'
    }
    if (/already|in progress|in flight|pending/.test(message)) {
        return 'already_in_flight'
    }
    if (/limit|too many|throttl/.test(message)) return 'rate_limited'
    return 'other'
}

/** True when this client can offer the native number-sharing sheet at all. */
export const canRequestContact = async (): Promise<boolean> => {
    const platform = resolvePlatform()
    if (!platform) return false
    const webApp = await loadSdk(platform)
    return typeof webApp?.requestContact === 'function'
}

export interface ContactResult {
    /** Signed `contact=…&auth_date=…&hash=…` payload; verifiable by the backend. */
    signedResponse?: string
    /** Unsigned local-format number. Pre-fill only — never proof. */
    phone?: string
}

/**
 * Show the native sheet and resolve with whatever the client gives us.
 *
 * Resolves to null if the user declined, the client refused, or it never
 * answered. Bale caps this at three requests per session and then throws, and
 * Eitaa throws if a request is already in flight — both are caught here, which
 * is why the manual field must always stay usable.
 */
export const requestPhoneFromMessenger = async (): Promise<ContactResult | null> => {
    const platform = resolvePlatform()
    // Both of these used to `return null` in silence, and between them they
    // swallowed about half of every tap on «استفاده از شمارهٔ همین حساب»: 103
    // taps produced only 52 outcome events, and the missing 51 were
    // indistinguishable from a user who simply declined. The button is only
    // offered when `canRequestContact()` said yes, so reaching either of these
    // means the two disagreed — which is worth knowing about.
    if (!platform) {
        posthog.capture('miniapp_request_contact_unavailable', {
            reason: 'no_platform',
        })
        return null
    }
    const webApp = await loadSdk(platform)
    const request = webApp?.requestContact
    if (!webApp || !request) {
        posthog.capture('miniapp_request_contact_unavailable', {
            platform,
            reason: webApp ? 'no_request_contact' : 'no_sdk',
        })
        return null
    }

    return new Promise<ContactResult | null>((resolve) => {
        let settled = false
        const finish = (value: ContactResult | null) => {
            if (settled) return
            settled = true
            resolve(value)
        }
        try {
            request.call(webApp, (shared: boolean, payload?: unknown) => {
                // Wrapped: this callback runs asynchronously, so anything that
                // throws in here would otherwise leave the promise pending
                // forever and the button spinning — which is exactly what an
                // earlier version did on Eitaa by assuming a string.
                try {
                    if (!shared) {
                        posthog.capture('miniapp_request_contact_not_shared', {
                            platform,
                        })
                        return finish(null)
                    }

                    if (typeof payload === 'string') {
                        return finish({ phone: toLocalMobile(payload) || undefined })
                    }
                    const event = (payload || {}) as {
                        response?: string
                        responseUnsafe?: { contact?: { phone_number?: string } }
                    }
                    // Distinguishes "user shared but the client sent us
                    // nothing" from "the client refused outright".
                    posthog.capture('miniapp_request_contact_shared', {
                        platform,
                        has_signed_response: !!event.response,
                        has_parsed_contact: !!event.responseUnsafe?.contact,
                    })
                    finish({
                        signedResponse: event.response,
                        phone:
                            toLocalMobile(
                                event.responseUnsafe?.contact?.phone_number
                            ) || undefined,
                    })
                } catch {
                    finish(null)
                }
            })
        } catch (error) {
            // The SDKs throw for several distinct reasons — unsupported client
            // version, a request already in flight, a per-session cap — and
            // they all look identical to the user. Record which.
            posthog.capture('miniapp_request_contact_threw', {
                platform,
                error_kind: classifyContactError(error),
            })
            finish(null)
        }
        // The sheet can be dismissed in ways that never fire the callback.
        window.setTimeout(() => {
            if (settled) return
            posthog.capture('miniapp_request_contact_timed_out', { platform })
            finish(null)
        }, REQUEST_TIMEOUT_MS)
    })
}
